import { registerHandler } from './MessageManager';

import utils = egr.wcf.utils;

type ActionTypeGuard<T extends string> = (value: string) => value is T;
function getTypeGuard<T extends string>(allowedValues: Array<string>): ActionTypeGuard<T> {
    return (value: string): value is T => {
        return allowedValues.indexOf(value) !== -1;
    };
}

export type SynchronizeAction = 'background' | 'foreground';
export type UserinteractionAction = 'back_pressed';
export interface OpenfileAction {
    error: boolean;
    type: 'pec' | 'pbox' | 'obk';
    path: string;
}

type ActionTypeGuardSignature<T extends string> = (value: string) => value is T;
const isSynchronizeAction: ActionTypeGuardSignature<SynchronizeAction> = getTypeGuard<SynchronizeAction>(['background', 'foreground']);
const isUserinteractionAction: ActionTypeGuardSignature<UserinteractionAction> = getTypeGuard<UserinteractionAction>(['back_pressed']);

export interface WebActionEvents {
    openfile: utils.AsyncListenableEvent<OpenfileAction, undefined>;
    synchronize: utils.AsyncListenableEvent<SynchronizeAction, undefined>;
    userinteraction: utils.AsyncListenableEvent<UserinteractionAction, undefined>;
    arUpdate: utils.AsyncListenableEvent<string, undefined>;
    arPlanesAdded: utils.AsyncListenableEvent<string, undefined>;
    arPlanesUpdated: utils.AsyncListenableEvent<string, undefined>;
    arPlanesRemoved: utils.AsyncListenableEvent<string, undefined>;
}

export const events: WebActionEvents = {
    openfile: new utils.AsyncListenableEvent<OpenfileAction, undefined>(undefined),
    synchronize: new utils.AsyncListenableEvent<SynchronizeAction, undefined>(undefined),
    userinteraction: new utils.AsyncListenableEvent<UserinteractionAction, undefined>(undefined),
    arUpdate: new utils.AsyncListenableEvent<string, undefined>(undefined),
    arPlanesAdded: new utils.AsyncListenableEvent<string, undefined>(undefined),
    arPlanesUpdated: new utils.AsyncListenableEvent<string, undefined>(undefined),
    arPlanesRemoved: new utils.AsyncListenableEvent<string, undefined>(undefined),
};

export type WebActionHandler = (event: MessageEvent) => void;

const RegisteredHandlers: Map<string, Array<WebActionHandler>> = new Map<string, Array<WebActionHandler>>();

function webactionMessageHandler(event: MessageEvent): void {
    const handlers: Array<WebActionHandler> | undefined = RegisteredHandlers.get(event.data.action);
    if (handlers != null) {
        for (const handler of handlers) {
            handler(event);
        }
    }
}

export function registerWebAction(name: string, handler: WebActionHandler): void {
    if (!RegisteredHandlers.has(name)) {
        RegisteredHandlers.set(name, []);
    }

    RegisteredHandlers.get(name)!.push(handler);
}

registerHandler({
    type: 'webaction',
    callback: webactionMessageHandler
});

registerWebAction(
    'openfile',
    (event: MessageEvent): void => {
        void events.openfile.trigger(event.data.arguments);
    }
);

registerWebAction(
    'synchronize',
    (event: MessageEvent): void => {
        if (isSynchronizeAction(event.data.arguments)) {
            void events.synchronize.trigger(event.data.arguments);
        }
    }
);

registerWebAction(
    'userinteraction',
    (event: MessageEvent): void => {
        if (isUserinteractionAction(event.data.arguments)) {
            void events.userinteraction.trigger(event.data.arguments);
        }
    }
);

function getArEventHandler(eventName: 'arUpdate' | 'arPlanesAdded' | 'arPlanesUpdated' | 'arPlanesRemoved'): WebActionHandler {
    return (event: MessageEvent): void => {
        void events[eventName].trigger(event.data.data);
    };
}

registerWebAction('ar_update', getArEventHandler('arUpdate'));
registerWebAction('ar_planesadded', getArEventHandler('arPlanesAdded'));
registerWebAction('ar_planesupdated', getArEventHandler('arPlanesUpdated'));
registerWebAction('ar_planesremoved', getArEventHandler('arPlanesRemoved'));

export default events;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/app-api/WebAction.ts